import orderBy from 'lodash.orderby'
import {ArrUtil, MapUtil, ObjUtil, Util, UUID} from 'yougile-util'
import {
  createSubtaskFromTask,
  DataObjectId,
  Hub,
  isDeletedEntity,
  isTaskWithoutPermissions,
  noBoardContainerId,
  rootNodeId,
  Subtask,
  SubtaskList
} from 'yougile-model'
import {Items} from '@api/model/items'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {globals} from '@extensions/globals-base'
import {StickerHub} from '@extensions/sticker/views-web/a-sticker-view'
import {Task} from '@extensions/task'

export class SubtaskUtil {
  private static parentsTaskIdCache: Map<DataObjectId, DataObjectId[]> // map subtask id to list of its parent ids
  private static subtaskIdsCache: Map<DataObjectId, DataObjectId[]> // map task id to list of its subtask ids

  private static listenerId: number

  private static newSubtasksListeners: Map<
    UUID,
    (ids: DataObjectId[]) => void
  > = new Map()
  private static removedSubtasksListeners: Map<
    UUID,
    (ids: DataObjectId[]) => void
  > = new Map()

  static updateCaches(parentId: DataObjectId): void {
    const oldChildrenIds = this.subtaskIdsCache.get(parentId) ?? []
    const task = Task.getById(parentId)
    if (!task) {
      return
    }
    const newChildrenIds = this.getSubtaskIds(task)
    const childrenDiff = ArrUtil.diff(oldChildrenIds, newChildrenIds)
    childrenDiff.remove.forEach(id =>
      MapUtil.update(
        this.parentsTaskIdCache,
        id,
        x => x && ArrUtil.delete(x, parentId)
      )
    )
    childrenDiff.add.forEach(id =>
      MapUtil.update(this.parentsTaskIdCache, id, x => [...(x ?? []), parentId])
    )

    if (childrenDiff.remove.length) {
      this.removedSubtasksListeners.forEach(listener =>
        listener(childrenDiff.remove)
      )
    }
    if (childrenDiff.add.length) {
      this.newSubtasksListeners.forEach(listener => listener(childrenDiff.add))
    }

    this.subtaskIdsCache.set(parentId, newChildrenIds)
  }

  private static updateCacheFull(): void {
    this.parentsTaskIdCache = new Map()
    this.subtaskIdsCache = new Map()
    const allTasks = Task.allTasksWithDeleted(globals.getCompanyId())
    allTasks.forEach(task => {
      const subtaskIds = this.getSubtaskIds(task)
      this.subtaskIdsCache.set(task.id, subtaskIds)
      subtaskIds.forEach(id => {
        MapUtil.update(this.parentsTaskIdCache, id, x => [
          ...(x ?? []),
          task.id
        ])
      })
    })
  }

  static addNewSubtasksListener(cb: (ids: DataObjectId[]) => void): UUID {
    const id = Util.UUID()
    this.newSubtasksListeners.set(id, cb)
    return id
  }
  static deleteNewSubtasksListener(listenerId?: UUID): void {
    listenerId && this.newSubtasksListeners.delete(listenerId)
  }

  static addRemovedSubtasksListener(cb: (ids: DataObjectId[]) => void): UUID {
    const id = Util.UUID()
    this.removedSubtasksListeners.set(id, cb)
    return id
  }
  static deleteRemovedSubtasksListener(listenerId?: UUID): void {
    listenerId && this.removedSubtasksListeners.delete(listenerId)
  }

  static getParentTaskIds(
    taskId: DataObjectId,
    force?: boolean
  ): DataObjectId[] {
    if (!this.parentsTaskIdCache || force) {
      this.updateCacheFull()
    }

    if (this.listenerId === undefined) {
      this.addSyncEventListenerForCache()
      globals.onCompanyChange(() => {
        this.updateCacheFull()
        this.addSyncEventListenerForCache()
      })
    }

    return (
      this.parentsTaskIdCache.get(taskId)?.filter(id => id !== taskId) ?? []
    )
  }

  static getAllParentTaskIds(taskId: DataObjectId): DataObjectId[] {
    const taskIds: DataObjectId[] = []

    const observedIds = new Set<DataObjectId>()
    let currentIds = [taskId]
    while (currentIds.length) {
      let newIds: DataObjectId[] = []
      for (const currentId of currentIds) {
        if (!observedIds.has(currentId)) {
          observedIds.add(currentId)

          taskIds.push(currentId)

          const parentIds = SubtaskUtil.getParentTaskIds(currentId)
          newIds = newIds.concat(parentIds)
        }
      }
      currentIds = newIds
    }

    return taskIds
  }

  static getSubtaskColumnIds(
    taskId: DataObjectId,
    level = 0,
    processedIds = new Set<DataObjectId>()
  ): DataObjectId[] | undefined {
    if (level > 20) {
      return undefined
    }

    if (processedIds.has(taskId)) {
      return undefined
    }

    processedIds.add(taskId)

    const task = Task.getById(taskId)

    if (!task && !SubtaskUtil.isSubtask(taskId)) {
      return undefined
    }

    const parentTasks = SubtaskUtil.getParentTaskIds(taskId)

    if (!parentTasks.length) {
      return undefined
    }

    const result: DataObjectId[] = []

    for (const parentId of parentTasks) {
      const taskLocation = Task.getLocations(parentId)
      if (taskLocation?.column) {
        result.push(taskLocation?.column.id)
      } else if (SubtaskUtil.isSubtask(parentId)) {
        result.push(
          ...(SubtaskUtil.getSubtaskColumnIds(
            parentId,
            level + 1,
            processedIds
          ) || [])
        )
      }
    }

    return result
  }

  static addSyncEventListenerForCache(): void {
    if (this.listenerId) {
      SyncEvents.removeListener(this.listenerId)
    }
    this.listenerId = SyncEvents.addListener(
      globals.getCompanyId(),
      '*',
      data => {
        data.forEach(x => x.ids.forEach(id => this.updateCaches(id)))
      }
    )
  }

  static convertToTask(task: Subtask): Hub {
    const hub = new Hub(globals.getCompanyId(), task.title)
    hub.id = task.id
    hub.complete = task.isArchived
    hub.location = task.location
    hub.stickers = task.stickers
    return hub
  }

  static setSubtaskList(task: Hub, subtaskList: SubtaskList | undefined): void {
    if (!Items.beforeUpdateSubtasksList(Items.fromHub(task))) {
      return
    }

    if (!subtaskList) {
      delete task.data.subtaskList
    } else {
      task.data.subtaskList = subtaskList
    }

    Task.commitWithCheckPhantom(task)
    Items.updateSubtasksList(Items.fromHub(task))
  }

  static getSubtaskList(task: Hub | undefined): SubtaskList {
    if (!task) {
      return {subtasks: {}, order: []}
    }
    const taskData = task?.data
    if (!taskData?.subtaskList) {
      taskData.subtaskList = {subtasks: {}, order: []}
    } else if (!taskData.subtaskList.subtasks) {
      taskData.subtaskList.subtasks = {}
      taskData.subtaskList.order = []
    } else if (!taskData.subtaskList.order) {
      taskData.subtaskList.order = []
    }
    return taskData.subtaskList
  }

  static getSubtasksFromList(
    list: SubtaskList,
    filterWithoutPermissions = false
  ): Subtask[] {
    if (!list) {
      return []
    }

    const subtasks = Object.entries(list.subtasks)
      .map(([id, subtask]) => {
        const task = Task.getById(id as DataObjectId)
        if (
          !task ||
          isDeletedEntity(task) ||
          (filterWithoutPermissions && isTaskWithoutPermissions(task))
        ) {
          return undefined
        }
        const subtaskFromTask = createSubtaskFromTask(task)
        return subtaskFromTask ?? subtask
      })
      .filter(Util.isDefined)
    return orderBy(subtasks, x => list.order.indexOf(x.id))
  }

  static getSubtasksFromListAsHub(list: SubtaskList): Hub[] {
    const subtasks = Object.keys(list.subtasks)
      .map(id => {
        const task = Task.getById(id as DataObjectId)
        if (!task || isDeletedEntity(task)) {
          return undefined
        }
        return task
      })
      .filter(Util.isDefined)
    return orderBy(subtasks, x => list.order.indexOf(x.id))
  }

  static deleteSubtask(
    subtaskList: SubtaskList,
    subtaskIds: DataObjectId[]
  ): SubtaskList {
    return {
      subtasks: ObjUtil.filter(
        subtaskList.subtasks,
        (id: DataObjectId, x) =>
          !subtaskIds.includes(x.id) && !subtaskIds.includes(id)
      ) as Record<string, Subtask>,
      order: subtaskList.order.filter(x => !subtaskIds.includes(x))
    }
  }

  static addSubtasks(subtaskList: SubtaskList, tasks: Hub[]): SubtaskList {
    const newSubtasks = Object.fromEntries(
      tasks.map(x => [x.id, createSubtaskFromTask(x)])
    )
    const subtasks = {...subtaskList.subtasks, ...newSubtasks}
    return {
      subtasks: subtasks as Record<string, Subtask>,
      order: ArrUtil.unique(
        [...subtaskList.order, ...tasks.map(x => x.id)],
        x => x
      )
    }
  }

  static getStickerHub(task: Subtask): StickerHub {
    return {
      id: task.id,
      stickers: task.stickers ?? {},
      location: task.location,
      companyId: globals.getCompanyId(),
      isCompleted: task.isCompleted
    }
  }

  static getSubtaskIds(task: Hub): DataObjectId[] {
    if (task?.data?.subtaskList) {
      return ObjUtil.keys(task.data.subtaskList.subtasks) as DataObjectId[]
    }
    return []
  }

  static hasSubtasks(task: Hub): boolean {
    return (
      SubtaskUtil.getSubtaskIds(task)
        .map(x => Task.getById(x))
        .filter(t => t && !isDeletedEntity(t)).length > 0
    )
  }

  static isParentOf(
    parentId: DataObjectId,
    childId: DataObjectId,
    recur = 0
  ): boolean {
    if (recur > 3) {
      return false
    }
    const box = Sync.getCurrentSandbox()
    const child = box.getHub(childId)
    if (!child) {
      return false
    }
    if (child.location === noBoardContainerId) {
      if (
        parentId === noBoardContainerId ||
        parentId === rootNodeId ||
        parentId === box.companyId
      ) {
        return true
      }
      const parentTasks = this.getParentTaskIds(child.id)
      for (const p of parentTasks) {
        if (this.isParentOf(parentId, p, recur + 1)) {
          return true
        }
      }
      return false
    }
    return box.isParentOf(parentId, child.id)
  }

  static getFirstParentWithLocation(
    hub?: Hub,
    level = 0,
    processed = new Set<DataObjectId>()
  ): Hub | undefined {
    if (!hub) {
      return hub
    }
    if (processed.has(hub.id)) {
      return hub
    }
    processed.add(hub.id)
    if (level > 10) {
      return hub
    }
    if (hub.location !== noBoardContainerId) {
      return hub
    }
    const box = Sync.getCurrentSandbox()
    const parentIds = SubtaskUtil.getParentTaskIds(hub.id)
    if (parentIds.length === 0) {
      return hub
    }
    for (const parentId of parentIds) {
      const parentHub = box.getHub(parentId)
      if (parentHub?.location !== noBoardContainerId) {
        return parentHub
      } else {
        return SubtaskUtil.getFirstParentWithLocation(
          parentHub,
          level + 1,
          processed
        )
      }
    }
    return hub
  }

  /**
   * TODO Fix this to support multiparent structure, first parent is checked only for now
   * TODO Try using Task.getAllColumnLevelParentTaskIds instead
   */
  static getTopParentTask(hub: Hub): Hub | undefined {
    const box = Sync.getCurrentSandbox()

    let child = hub
    let i = 0
    while (i < 10) {
      const [parentId] = SubtaskUtil.getParentTaskIds(child.id)
      const parent = box.getHub(parentId)
      if (!parent) {
        return child
      }

      child = parent
      i++
    }

    return undefined
  }

  static isSubtask(hubId: DataObjectId): boolean {
    return this.getParentTaskIds(hubId).length > 0
  }
}
